import {onError} from '@apollo/client/link/error';
import type {GraphQLFormattedError} from 'graphql';

import notifier from '@albert-io/notifier';

import {ERROR_KIND, type ErrorKind, getGraphQLErrorCode, getKindFromCode} from 'lib/normalizeError';

// mandark puts code/short_message/fields/vars at the top level, not in extensions.
type WireGraphQLError = GraphQLFormattedError & {
  code?: unknown;
  short_message?: unknown;
  fields?: unknown;
  vars?: unknown;
};

/**
 * Keys to strip from GraphQL variables before sending to Honeybadger,
 * as a safeguard against PII leaking into error reports.
 * Aligned with the CONTENT_FILTERS list in the Honeybadger browser config,
 * plus camelCase variants used in GraphQL variable conventions.
 */
const SENSITIVE_VARIABLE_KEYS = new Set([
  'token',
  'email',
  'username',
  'password',
  'oldPassword',
  'newPassword',
  'verify_old_password',
  'verifyOldPassword',
  'password_reset_link',
  'passwordResetLink',
  'verify_password_reset_link',
  'verifyPasswordResetLink',
  'stripe_customer_id',
  'stripeCustomerId',
  'first_name',
  'firstName',
  'last_name',
  'lastName',
  'salutation',
  'consent_email',
  'consentEmail'
]);

/**
 * Explicit list of non-actionable GraphQL error codes emitted by ash_graphql.
 * These are lowercase snake_case because ash_graphql emits them that way
 * (see https://github.com/ash-project/ash_graphql/blob/main/lib/error.ex).
 *
 * Grouped:
 *  - Validation: user-facing input errors, already surfaced in the UI.
 *  - Authorization: expired sessions, permission denials — not FE bugs.
 *  - Not found: often user navigated to a stale resource — not actionable.
 */
const NON_ACTIONABLE_ASH_CODES = new Set<string>([
  // validation
  'invalid_changes',
  'invalid_query',
  'invalid_keyset',
  'invalid_attribute',
  'invalid_argument',
  'invalid_primary_key',
  'required',
  // authorization
  'forbidden',
  'forbidden_field',
  'authentication_failed',
  'invalid_token',
  // not found
  'not_found'
]);

/**
 * Backend catch-all errors contain a unique error ID for server-side tracing
 * but provide no actionable information to the frontend. These are already
 * logged and tracked on the backend via the UUID — reporting them to HB
 * from the client only creates noise.
 *
 * The backend wraps the UUID in backticks, so the optional `` `? `` around the
 * id (and trailing `\s*`) keep this matching both the wrapped and bare forms.
 *
 * Pattern: "Something went wrong. Unique error id: `<uuid>`"
 *
 * The id portion (`[\da-f-]+`) is intentionally loose rather than a strict UUID
 * match — the literal prefix already scopes this to the backend catch-all.
 */
const BACKEND_CATCH_ALL_RE = /^Something went wrong\.\s*Unique error id:\s*`?[\da-f-]+`?\s*$/i;

/**
 * Error kinds (from `lib/normalizeError.ts`) that are non-actionable from the
 * frontend. This is the fallback filter for errors using non-ash codes
 * (e.g. `UNAUTHENTICATED`, `BAD_REQUEST`, `RATE_LIMITED`) that may come from
 * custom resolvers or upstream services.
 */
const NON_ACTIONABLE_KINDS = new Set<ErrorKind>([
  ERROR_KIND.UNAUTHORIZED,
  ERROR_KIND.FORBIDDEN,
  ERROR_KIND.NOT_FOUND,
  ERROR_KIND.VALIDATION,
  ERROR_KIND.RATE_LIMITED
]);

/**
 * Maximum number of fingerprints to retain in the session dedup set.
 * With code-first fingerprinting the realistic cardinality is
 * O(unique operations × unique error codes), well below this cap.
 * If the cap is reached the set is cleared, allowing one re-report
 * per fingerprint — a safe trade-off vs unbounded memory growth.
 */
const MAX_SEEN_FINGERPRINTS = 500;

/**
 * Module-level set of fingerprints already reported in this page/session.
 * Cleared only by a page reload. Prevents the same error from a repeated
 * operation (e.g. user retries, polling) from burning through Honeybadger
 * event quota.
 */
const seenFingerprints = new Set<string>();

/**
 * Clears the module-level dedup set.
 * Kept internal so it is not exposed as part of the normal production API.
 */
function resetSeenFingerprints(): void {
  seenFingerprints.clear();
}

/**
 * Test-only helpers for resetting module state between tests.
 */
export const __testOnly = {
  resetSeenFingerprints
};

/**
 * Recursively strips known-sensitive keys from a value.
 * Handles nested objects and arrays at any depth.
 */
function sanitizeValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) =>
        SENSITIVE_VARIABLE_KEYS.has(key) ? [key, '[FILTERED]'] : [key, sanitizeValue(val)]
      )
    );
  }

  return value;
}

/**
 * Recursively strips known-sensitive keys from a variables object.
 * Returns a deep copy with sensitive values replaced by '[FILTERED]'.
 */
export function sanitizeVariables(
  variables: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!variables) return undefined;

  return sanitizeValue(variables) as Record<string, unknown>;
}

/**
 * Returns true if a GraphQL error's `extensions.code` is non-actionable.
 *
 * Checks two layers:
 *  1. Direct match against known ash_graphql codes (`invalid_argument`,
 *     `forbidden`, `not_found`, etc.) — these are the vast majority of errors
 *     from the Mandark backend.
 *  2. Fallback: normalized kind lookup via `getKindFromCode` for codes
 *     emitted by custom resolvers or upstream services (e.g. SCREAMING_SNAKE
 *     conventions like `UNAUTHENTICATED`, `BAD_REQUEST`).
 *
 * Unknown/missing codes are treated as actionable (fail-open — report).
 */
function isNonActionable(code: unknown): boolean {
  if (typeof code !== 'string') return false;
  if (NON_ACTIONABLE_ASH_CODES.has(code)) return true;
  const kind = getKindFromCode(code);
  return kind !== null && NON_ACTIONABLE_KINDS.has(kind);
}

// Explicit picks (a spread pulls in GraphQLError AST nodes), sanitized as a whole.
function serializeGraphQLError(gqlError: WireGraphQLError): Record<string, unknown> {
  return sanitizeValue({
    message: gqlError.message,
    path: gqlError.path,
    extensions: gqlError.extensions,
    ...(typeof gqlError.code === 'string' ? {code: gqlError.code} : {}),
    ...(typeof gqlError.short_message === 'string' ? {short_message: gqlError.short_message} : {}),
    ...(gqlError.fields !== undefined ? {fields: gqlError.fields} : {}),
    ...(gqlError.vars !== undefined ? {vars: gqlError.vars} : {})
  }) as Record<string, unknown>;
}

/**
 * Apollo error link that reports GraphQL response errors to Honeybadger.
 *
 * Only reports errors from the GraphQL `errors` response array (application-level
 * errors). Network errors (connection failures, timeouts, HTTP status errors) are
 * intentionally excluded — they are transient, not actionable from the frontend,
 * and are already monitored by backend logging.
 *
 * Volume controls (stacked):
 *  1. Category filter — skips errors whose `extensions.code` maps to a
 *     non-actionable kind (auth, validation, not-found, rate-limit).
 *  2. Aggregation — emits one notify per operation invocation with all
 *     reportable errors in `context.errors[]`, instead of one per error.
 *  3. Session dedup — skips notify if the (operation, first-message)
 *     fingerprint was already reported since page load.
 *
 * - Tagged `graphql-error`
 * - Component: `apollo-graphql`
 * - Fingerprinted by operation name + first error message for grouping
 *
 * This link never swallows errors — they still propagate to components.
 */
// TODO(AC4): `graphQLErrors` is deprecated in @apollo/client 3.14+ and will be removed
// in Apollo Client 4.0. It is consolidated into a single `error` property. When upgrading
// to AC4, migrate this callback to use `error` instead.
// See: https://www.apollographql.com/docs/react/migrating/apollo-client-4
const errorLink = onError(({graphQLErrors, operation}) => {
  if (!graphQLErrors || graphQLErrors.length === 0) {
    return;
  }

  const reportable = graphQLErrors.filter(
    (gqlError) =>
      // Skip message-less entries; they only yield "...: undefined" noise.
      Boolean(gqlError.message) &&
      !isNonActionable(getGraphQLErrorCode(gqlError)) &&
      !BACKEND_CATCH_ALL_RE.test(gqlError.message)
  );

  if (reportable.length === 0) {
    return;
  }

  const operationName = operation.operationName || 'UnknownOperation';
  const primary = reportable[0];
  const fpKey = getGraphQLErrorCode(primary) ?? primary.message;
  const fingerprint = `graphql-error-${operationName}-${fpKey}`;

  if (seenFingerprints.has(fingerprint)) {
    return;
  }
  if (seenFingerprints.size >= MAX_SEEN_FINGERPRINTS) {
    seenFingerprints.clear();
  }
  seenFingerprints.add(fingerprint);

  const operationDefinition = operation.query.definitions.find(
    (def) => def.kind === 'OperationDefinition'
  );
  const operationType =
    operationDefinition?.kind === 'OperationDefinition' ? operationDefinition.operation : 'unknown';

  notifier.notify(new Error(primary.message), {
    message: `GraphQL error in ${operationName}: ${primary.message}`,
    tags: ['graphql-error'],
    component: 'apollo-graphql',
    fingerprint,
    context: {
      operationName,
      operationType,
      errorCount: reportable.length,
      errors: reportable.map((gqlError) => serializeGraphQLError(gqlError)),
      variables: sanitizeVariables(operation.variables)
    }
  });
});

export default errorLink;
